import { inject, Injectable } from '@angular/core';
import { BaseModelKeys } from '@ag-common-lib/lib/models/base.model';
import {
  AgencyBrandingKeys,
  createAgencyBranding,
  createAgencyBrandingLogo,
  mapAgencyBrandingFromApi,
  mapAgencyBrandingToApi,
  type AgencyBranding,
} from '@ag-common-lib/lib/models/utils/agency-branding.model';
import { HeadshotKeys } from '@ag-common-lib/lib/models/utils/headshot.model';
import type { AgencyBrandingPayload } from '@ag-common-lib/lib/contracts/reporting-api/document/agency-branding';
import type { AgencyBrandingDetailsResponse } from '@ag-common-lib/lib/contracts/reporting-api/response/agency-branding';
import { AgencyMedia, AGMedia, AGMediaKeys, ImageCropperPayload } from '@ag-common-lib/public-api';
import { AgencyImagesService } from 'ag-common-svc/lib/services/agency-images.service';
import { ReportingApiService } from 'ag-common-svc/lib/services/reporting-api.service';
import { ToastrService } from 'ngx-toastr';

/**
 * Branding helper.
 * Load via GET; persist via PUT only on explicit Save.
 * Logo upload/crop updates local draft for preview only.
 */
@Injectable()
export class AgencyBrandingService {
  private readonly basePath = '/api/agency-branding';
  private readonly agencyImagesService = inject(AgencyImagesService);
  private readonly reportingApiService = inject(ReportingApiService);
  private readonly toastrService = inject(ToastrService);

  getByAgencyId = async (agencyId: string): Promise<Required<AgencyBranding>> => {
    const response = await this.reportingApiService.request<AgencyBrandingDetailsResponse>(
      `${this.basePath}/${encodeURIComponent(agencyId)}`,
    );

    return createAgencyBranding(mapAgencyBrandingFromApi(response.data));
  };

  updateByAgencyId = async (
    agencyId: string,
    branding: AgencyBranding,
    options?: { showSuccessToast?: boolean },
  ): Promise<Required<AgencyBranding>> => {
    const payload: AgencyBrandingPayload = mapAgencyBrandingToApi(branding);
    const response = await this.reportingApiService.request<AgencyBrandingDetailsResponse>(
      `${this.basePath}/${encodeURIComponent(agencyId)}`,
      {
        method: 'PUT',
        body: payload,
      },
    );

    if (options?.showSuccessToast) {
      this.toastrService.success('Agency branding successfully updated');
    }

    return createAgencyBranding(mapAgencyBrandingFromApi(response.data));
  };

  normalize(branding: AgencyBranding): Required<AgencyBranding> {
    return createAgencyBranding(branding);
  }

  applyImageCropped = (
    branding: AgencyBranding | null | undefined,
    imageCropperPayload: ImageCropperPayload,
  ): Required<AgencyBranding> => {
    const nextBranding = createAgencyBranding(branding);
    nextBranding[AgencyBrandingKeys.logo] = {
      ...createAgencyBrandingLogo(nextBranding[AgencyBrandingKeys.logo]),
      [HeadshotKeys.imageCropperPayload]: imageCropperPayload,
    };

    return nextBranding;
  };

  applyLogoImageChanged = async (
    agencyDbId: string,
    branding: AgencyBranding | null | undefined,
    mediaUrl: AGMedia,
  ): Promise<Required<AgencyBranding>> => {
    if (!mediaUrl?.[BaseModelKeys.dbId]) {
      const media = Object.assign({}, new AgencyMedia(agencyDbId), {
        [AGMediaKeys.contentType]: mediaUrl?.contentType ?? null,
        [AGMediaKeys.wasabiPath]: mediaUrl?.wasabiPath ?? null,
        [AGMediaKeys.fileName]: mediaUrl?.fileName ?? null,
      });

      const response = await this.agencyImagesService.addAgencyMediaToImagesDirectory(agencyDbId, media);
      Object.assign(mediaUrl, response);
    }

    const nextBranding = createAgencyBranding(branding);
    nextBranding[AgencyBrandingKeys.logo] = {
      [HeadshotKeys.wasabiPath]: mediaUrl?.[AGMediaKeys.wasabiPath] ?? '',
      [HeadshotKeys.imageCropperPayload]: null,
    };

    return nextBranding;
  };
}
